import React, { useState } from "react";
import { motion, AnimatePresence } from 'framer-motion';
import { Props_SnippetImageRow } from "./ImageRow.props";
import SharedHeading from "../Shared/Heading/Heading";
import { CompileMarkdown } from '../../../../Shared/AttraxTs/React/ReactSnippetMDCompiler';

const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = React.useState(typeof window !== 'undefined' ? window.innerWidth > 991 : true);
  React.useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth > 991);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isDesktop;
};

const ImageRow = (props: Props_SnippetImageRow) => {
  const { heading, repeatable_items } = props;
  const [openIndex, setOpenIndex] = useState<number>(-1);
  const isDesktop = useIsDesktop();
      const headingLevel = heading.level?.value as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' || 'h2';

  return (
    <section className="snippet-image-row">
      <div className="snippet-image-row__container">
        <div className="snippet-image-row__heading-section">
          <SharedHeading
            className="snippet-image-row__heading"
            alignment={
              heading.alignment?.value === "start" ||
              heading.alignment?.value === "center" ||
              heading.alignment?.value === "end"
                ? heading.alignment.value
                : undefined
            }
            level={headingLevel}
            visibility={typeof heading.visibility?.value === 'boolean' ? heading.visibility.value : heading.visibility?.value === 'true'}
          >
            {heading.text?.value}
          </SharedHeading>
        </div>
        <div className="snippet-image-row__items">
          {repeatable_items?.map((item, idx) => {
            const imageSrc = item.image?.value;
            const imageAlt = item.imageAlt?.value || "";
            const buttonText = item.buttonText?.value || "Toggle";
            const content = item.content?.value || "";
            const isOpen = openIndex === idx;
            const styleClass = item.style?.value ? `snippet-image-row__item--${item.style.value}` : "";
            return (
              <div className={`snippet-image-row__item ${styleClass}`} key={idx}>
                {imageSrc && (
                  <img
                    src={imageSrc}
                    alt={imageAlt}
                    className="snippet-image-row__image"
                    loading="lazy"
                  />
                )}
                <div className={`snippet-image-row__content-container` + (isDesktop  && isOpen ? " snippet-image-row__content-container--opened" : "")}>
                <button
                  className={`snippet-image-row__button${isDesktop && isOpen ? " snippet-image-row__button--active" : ""}`}
                  aria-expanded={isDesktop ? isOpen : true}
                  aria-controls={`image-row-panel-${idx}`}
                  id={`image-row-header-${idx}`}
                  onClick={isDesktop ? () => setOpenIndex(isOpen ? -1 : idx) : undefined}
                  type="button"
                  tabIndex={isDesktop ? 0 : -1}
                  aria-disabled={!isDesktop}
                >
                  {buttonText}
                </button>
                <AnimatePresence initial={false}>
                  {(isDesktop ? isOpen : true) && (
                    <motion.div
                      id={`image-row-panel-${idx}`}
                      role="region"
                      aria-labelledby={`image-row-header-${idx}`}
                      className="snippet-image-row__panel"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ 
                        duration: 0.4,
                        ease: [0.04, 0.62, 0.23, 0.98],
                        height: { duration: 0.4 },
                        opacity: { duration: 0.25, delay: isOpen ? 0.1 : 0 }
                      }}
                      style={{ overflow: 'hidden' }}
                    >
                      <div className="snippet-image-row__panel-content">
                        {CompileMarkdown(content)}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence></div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ImageRow;
